import { Transaction } from '../types';

/** Wallet filter values for "All Portfolios" and the no-wallet bucket. */
export const ALL_WALLETS = 'all';
export const UNSPECIFIED = '__unspecified__';

/**
 * The transactions that make up one wallet, as the Holdings wallet filter sees it.
 *
 * Transfers between wallets are expanded into a virtual SELL on the source wallet
 * and a virtual BUY on the destination wallet, priced at the global avg buy price
 * for the coin (so cost basis transfers cleanly between wallets). Pure, so the
 * per-wallet chart's tests can check its last point against these exact rows.
 */
export function walletScopedTransactions(transactions: Transaction[], walletFilter: string): Transaction[] {
  // Global avg buy price per coin — used as the synthetic price for transfer legs
  const coinCostAgg: Record<string, { totalCost: number; amount: number }> = {};
  for (const tx of transactions) {
    if (tx.type !== 'BUY') continue;
    if (!coinCostAgg[tx.coinId]) coinCostAgg[tx.coinId] = { totalCost: 0, amount: 0 };
    coinCostAgg[tx.coinId].totalCost += tx.amount * tx.price;
    coinCostAgg[tx.coinId].amount    += tx.amount;
  }
  const avgPriceFor = (coinId: string) => {
    const c = coinCostAgg[coinId];
    return c && c.amount > 0 ? c.totalCost / c.amount : 0;
  };

  const filteredTxs: Transaction[] = [];
  for (const tx of transactions) {
    if (tx.type === 'TRANSFER') {
      // Transfers don't apply to the "Unspecified" bucket
      if (walletFilter === UNSPECIFIED) continue;
      const fromMatch = tx.fromWallet === walletFilter;
      const toMatch   = tx.toWallet   === walletFilter;
      if (!fromMatch && !toMatch) continue;
      const synthPrice = avgPriceFor(tx.coinId);
      if (fromMatch) {
        filteredTxs.push({ ...tx, type: 'SELL', wallet: walletFilter, price: synthPrice });
      }
      if (toMatch) {
        filteredTxs.push({ ...tx, type: 'BUY',  wallet: walletFilter, price: synthPrice });
      }
      continue;
    }
    const w = tx.wallet?.trim();
    if (walletFilter === UNSPECIFIED ? !w : w === walletFilter) {
      filteredTxs.push(tx);
    }
  }
  return filteredTxs;
}
